/**
 * csvPreview.ts — CSV ファイルの FE 事前検証パーサ共通 util
 *
 * 判定式（cmd-po_1132 定義・BE input_csv/app.py L629-648 と同一式）:
 *   encoding  = FF FE → 'utf-16le' / FE FF → 'utf-16be'
 *             / TextDecoder('utf-8',{fatal:true}) 成功 → 'utf-8' / otherwise → 'shift-jis'
 *   delimiter = (utf-16le|utf-16be) ? '\t' : ','
 * BOM 除去は TextDecoder に任せる（ignoreBOM デフォルト=false）。
 */

export type CsvEncoding = "utf-16le" | "utf-16be" | "utf-8" | "shift-jis";
export type CsvDelimiter = "\t" | ",";

export function detectCsvEncoding(input: ArrayBuffer | Uint8Array): CsvEncoding {
  const u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (u8.length >= 2 && u8[0] === 0xff && u8[1] === 0xfe) return "utf-16le";
  if (u8.length >= 2 && u8[0] === 0xfe && u8[1] === 0xff) return "utf-16be";
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(u8);
    return "utf-8";
  } catch {
    return "shift-jis";
  }
}

export function csvDelimiterFor(encoding: CsvEncoding): CsvDelimiter {
  return encoding === "utf-16le" || encoding === "utf-16be" ? "\t" : ",";
}

export function splitCsvLine(line: string, delimiter: CsvDelimiter): string[] {
  return line.split(delimiter).map((c) => c.replace(/^"|"$/g, "").trim());
}

export function parseCsvBufferForPreview(input: ArrayBuffer | Uint8Array): {
  rowCount: number;
  headers: string[];
  sample: string[][];
} {
  const u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
  const encoding = detectCsvEncoding(u8);
  const text = new TextDecoder(encoding).decode(u8);
  const delimiter = csvDelimiterFor(encoding);
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { rowCount: 0, headers: [], sample: [] };
  const headers = splitCsvLine(lines[0], delimiter);
  const dataLines = lines.slice(1);
  return {
    rowCount: dataLines.length,
    headers,
    sample: dataLines.slice(0, 3).map((l) => splitCsvLine(l, delimiter)),
  };
}

export async function parseCSVForPreview(file: File): Promise<{
  rowCount: number;
  headers: string[];
  sample: string[][];
}> {
  return parseCsvBufferForPreview(await file.arrayBuffer());
}
